import { useState } from "react";
import { cyclePriceOf, useAdminData } from "../state/AdminDataContext";
import type { Plan } from "../lib/adminTypes";
import { mmk } from "../lib/format";
import { figure } from "@cliqsuite/ui";
import { PlanModal, type PlanFormValue } from "../components/PlanModal";
import { ConfirmModal } from "../components/Modal";
import { BillingCyclesPanel } from "../components/BillingCyclesPanel";
import { PageHeaderActions } from "../components/PageHeaderActions";

const limit = (n: number, word: string) => (n >= 999 ? `Unlimited ${word}` : `${n} ${word}`);

export function PlansPage() {
  const { plans, cycles, planSubscriberCounts, planSave, planArchive } = useAdminData();
  const [modal, setModal] = useState<{ key: string | null; create: boolean } | null>(null);
  const [archiving, setArchiving] = useState<Plan | null>(null);
  const modalPlan: Plan | null = modal && modal.key ? (plans.find((p) => p.key === modal.key) ?? null) : null;
  const live = plans.filter((p) => !p.archived);
  const archived = plans.filter((p) => p.archived);
  // The most popular live plan gets the ink card, as on the canvas.
  const featured = live.reduce<Plan | null>((best, p) => ((planSubscriberCounts[p.key] ?? 0) > (best ? (planSubscriberCounts[best.key] ?? 0) : -1) ? p : best), null);

  const handleSubmit = (form: PlanFormValue) => {
    planSave(modal?.key ?? null, form);
    setModal(null);
  };

  return (
    <div>
      <PageHeaderActions>
        <button type="button" className="o-btn o-btn--ink" onClick={() => setModal({ key: null, create: true })}>
          <span className="ms" aria-hidden="true">add</span>
          New plan
        </button>
      </PageHeaderActions>

      <div className="o-modules" style={{ marginBottom: 16 }}>
        {live.map((p) => {
          const ink = p.key === featured?.key;
          const count = planSubscriberCounts[p.key] ?? 0;
          return (
            <div key={p.key} className={`o-plan${ink ? " is-current" : ""}`} style={{ gap: 10, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 17, fontWeight: 800 }}>{p.name}</span>
                <button type="button" className="o-icon-btn" aria-label={`Edit ${p.name}`} style={ink ? { background: "rgba(255,255,255,.14)", color: "#fff" } : undefined} onClick={() => setModal({ key: p.key, create: false })}>
                  <span className="ms" aria-hidden="true">edit</span>
                </button>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, opacity: 0.7 }}>{p.blurb || (p.custom ? "Set per business" : "")}</div>
              <div className="num" style={{ fontSize: 22, fontWeight: 800 }}>
                {p.custom ? "Custom" : figure(mmk(p.price))}
                {!p.custom && <span style={{ fontSize: 12.5, opacity: 0.6 }}> / mo</span>}
              </div>
              {!p.custom && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5, fontWeight: 700 }}>
                  {cycles.filter((c) => c.months > 1).map((c) => (
                    <div key={c.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ opacity: 0.7 }}>{c.label}</span>
                      <span className="num" style={{ fontWeight: 800 }}>
                        {mmk(cyclePriceOf(p, c))}
                        {p.cyclePrices[c.key] != null && (
                          <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 8, marginLeft: 6, background: ink ? "rgba(255,255,255,.14)" : "var(--coral-soft, #FFF1F4)", color: ink ? "#FFB3C0" : "var(--coral)" }}>Own price</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 14, fontSize: 12.5, fontWeight: 800, opacity: 0.8 }}>
                <span>{limit(p.stores, "stores")}</span>
                <span>{limit(p.seats, "seats")}</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 800, color: ink ? "#FFB3C0" : "var(--coral)" }}>
                {count} business{count === 1 ? "" : "es"} on plan
              </div>
            </div>
          );
        })}
      </div>

      <div className="o-split" style={{ gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)" }}>
        <BillingCyclesPanel />
        <section className="o-card">
          <div className="o-card__top">
            <div style={{ flex: 1 }}>
              <h2>Archived plans</h2>
              <div className="o-card__sub">Businesses already on them keep their price</div>
            </div>
          </div>
          {archived.length === 0 && <div className="o-muted-note">Nothing archived.</div>}
          {archived.map((p) => (
            <div key={p.key} className="o-list-row">
              <span className="o-ico"><span className="ms" aria-hidden="true">inventory_2</span></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="o-row__title">{p.name}</div>
                <div className="o-row__sub">
                  {mmk(p.price)} a month, {planSubscriberCounts[p.key] ?? 0} still on it
                </div>
              </div>
              <button type="button" className="o-btn o-btn--grey" style={{ height: 38, fontSize: 13 }} onClick={() => planArchive(p.key, false)}>
                Restore
              </button>
            </div>
          ))}
          {live.length > 0 && (
            <div className="o-row__sub" style={{ marginTop: 12, whiteSpace: "normal" }}>
              To archive a plan, open it with the edit button.
            </div>
          )}
        </section>
      </div>

      {modal && (
        <PlanModal
          plan={modalPlan}
          isCreate={modal.create}
          onClose={() => setModal(null)}
          onSubmit={handleSubmit}
          onArchive={modalPlan && !modalPlan.archived ? () => setArchiving(modalPlan) : undefined}
        />
      )}
      {archiving && (
        <ConfirmModal
          title={`Archive ${archiving.name}?`}
          body={`It stops being offered. The ${planSubscriberCounts[archiving.key] ?? 0} businesses on it keep their price.`}
          action="Archive"
          onClose={() => setArchiving(null)}
          onConfirm={() => {
            planArchive(archiving.key, true);
            setArchiving(null);
            setModal(null);
          }}
        />
      )}
    </div>
  );
}
